import {
	HELP_TABS,
	useMipCreateHelpModalController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MipCreateHelpModal/useMipCreateHelpModalController';
import { UIElementIdentifier } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { AssetsLoader } from '@dealstack/frontend/shared/components/AssetsLoader/AssetsLoader';
import { IconButton } from '@dealstack/frontend/shared/components/fundament/IconButton/IconButton';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { Tab, Tabs } from '@dealstack/frontend/shared/components/Tabs/Tabs';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { memo } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
	onClose: () => unknown;
}

const MipCreateHelpModal = ({ onClose, className }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const { activeTab, setActiveTab, onPrevArrowClick, onNextArrowClick } =
		useMipCreateHelpModalController();

	const isPrevButtonDisabled = activeTab.tabKey === HELP_TABS[0]?.tabKey;
	const isNextButtonDisabled = activeTab.tabKey === HELP_TABS[HELP_TABS.length - 1]?.tabKey;

	return (
		<Modal size="lg" onClose={onClose} className={className}>
			<SectionHeader
				title={tMipCreate('help_modal.title')}
				subtitle={tMipCreate('help_modal.subtitle')}
			/>
			<TabsWrapper size="large">
				{HELP_TABS.map((tab, index) => {
					const isActive = tab.tabKey === activeTab.tabKey;
					return (
						<UIElementIdentifier
							identifier={TEST_ELEMENT_IDS.MIP_CREATE_HELP_MODAL_TAB}
							index={index}
							key={index}
						>
							<Tab
								active={isActive}
								onClick={() => setActiveTab(tab)}
								key={tab.tabKey}
							>
								{tMipCreate(tab.title)}
							</Tab>
						</UIElementIdentifier>
					);
				})}
			</TabsWrapper>

			<TabContent>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.MIP_CREATE_HELP_MODAL_PREV_ARROW}>
					<IconButton
						onClick={onPrevArrowClick}
						icon="chevron-left"
						variant="filled"
						round
						disabled={isPrevButtonDisabled}
					/>
				</UIElementIdentifier>
				<TabDescription>
					<>
						<SectionHeader
							title={tMipCreate(activeTab.content.title)}
							overtitle={tMipCreate(activeTab.content.overtitle)}
						/>
						{activeTab.content.CustomContent ? (
							<activeTab.content.CustomContent />
						) : (
							<Text colorFaded variant="p-lg">
								{activeTab.content.text !== ''
									? tMipCreate(activeTab.content.text)
									: null}
							</Text>
						)}
					</>
				</TabDescription>
				<ImageWrapper>
					<AssetsLoader src={activeTab.content.imageSrc} />
				</ImageWrapper>
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.MIP_CREATE_HELP_MODAL_NEXT_ARROW}>
					<IconButton
						onClick={onNextArrowClick}
						icon="chevron-right"
						variant="filled"
						round
						disabled={isNextButtonDisabled}
					/>
				</UIElementIdentifier>
			</TabContent>
		</Modal>
	);
};

const TabsWrapper = styled(Tabs)`
	border-bottom: 1px solid ${({ theme }) => theme.v2.color.neutral[300]};
	${Tab} {
		padding: 1.5em 2em;
		width: 100%;
	}
`;

const TabContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 2.5em;
	margin: 2em 0;
`;

const TabDescription = styled.div`
	flex-grow: 1;
	${SectionHeader} {
		margin-bottom: 0.8em;
	}
`;

const ImageWrapper = styled.div`
	min-width: 30rem;
	max-width: 30rem;
	height: 21.5rem;
	img {
		width: 100%;
		height: 100%;
	}
`;

const Memoized = memo(MipCreateHelpModal);
const Styled = styled(Memoized)`
	${TabsWrapper} {
		margin-top: 1em;
	}
`;

export { Styled as MipCreateHelpModal };
